import { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setTimeOfDay } from "../../../store/slices/sessionSlice";
import { db } from "../../../config/firebase";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  where,
  Timestamp,
} from "firebase/firestore";

/**
 * Custom Hook for managing history data and related state
 * Separates data logic from UI components for better maintainability
 */
export const useHistoryData = (timePeriod = "all") => {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.user);

  const [darts, setDarts] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Set session time on mount
  useEffect(() => {
    const currentTime = new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
    dispatch(setTimeOfDay(currentTime));
  }, [dispatch]);

  // Load darts data
  useEffect(() => {
    if (!userInfo?.uid) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const col = collection(db, "userInfo", userInfo.uid, "darts");
    const q = query(col, orderBy("timestamp", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const dartsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setDarts(dartsData);
        setError(null);
      },
      (err) => {
        console.error("Error loading darts:", err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userInfo]);

  // Load sessions data
  useEffect(() => {
    if (!userInfo?.uid) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const col = collection(db, "userInfo", userInfo.uid, "sessions");
    let q;

    // Filter sessions based on selected time period
    if (timePeriod !== "all") {
      const now = new Date();
      let startDate;

      switch (timePeriod) {
        case "week":
          startDate = new Date(now);
          startDate.setDate(now.getDate() - 7);
          break;
        case "month":
          startDate = new Date(now);
          startDate.setMonth(now.getMonth() - 1);
          break;
        case "year":
          startDate = new Date(now);
          startDate.setFullYear(now.getFullYear() - 1);
          break;
        default:
          startDate = new Date(0); // Default to all time
      }

      q = query(
        col,
        where("createdAt", ">=", Timestamp.fromDate(startDate)),
        orderBy("createdAt", "desc")
      );
    } else {
      q = query(col, orderBy("createdAt", "desc"));
    }

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const sessionsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSessions(sessionsData);
        setError(null);
        setLoading(false);
      },
      (err) => {
        console.error("Error loading sessions:", err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userInfo, timePeriod]);

  // Utility function to find dart by reference
  const findDartById = (ref) => {
    const id = ref?.id || ref;
    return darts.find((dart) => dart.id === id) || null;
  };

  // Computed statistics
  const statistics = useMemo(() => {
    if (!sessions.length) {
      return {
        totalGames: 0,
        averageScore: 0,
        averages: [],
        trend: { direction: "stable", color: "text-white", icon: "FaMinus" },
      };
    }

    const averages = sessions.map(
      (session) => session.statistics?.avg_median || 0
    );
    const averageScore =
      averages.reduce((sum, val) => sum + val, 0) / averages.length;

    // Calculate trend
    let trend = { direction: "stable", color: "text-white", icon: "FaMinus" };

    if (averages.length >= 2) {
      if (averages[0] > averages[1]) {
        trend = { direction: "up", color: "text-green-500", icon: "FaArrowUp" };
      } else if (averages[0] < averages[1]) {
        trend = {
          direction: "down",
          color: "text-red-500",
          icon: "FaArrowDown",
        };
      }
    }

    return {
      totalGames: sessions.length,
      averageScore,
      averages,
      trend,
    };
  }, [sessions]);

  return {
    darts,
    sessions,
    loading,
    error,
    statistics,
    findDartById,
  };
};
